import type { BenefitDetailDto, BenefitWorkflowResponse } from "@/lib/benefit-types"
import type { IncomeAssessment } from "@/lib/income-ledger"

export type WorkspacePart<T> = { status: "available"; value: T; code?: null }
  | { status: "unavailable"; value: null; code: string }
export interface BenefitWorkspace {
  contractVersion: 1
  scopeKey: string
  readStamp: string
  benefit: BenefitDetailDto
  workflow: WorkspacePart<BenefitWorkflowResponse>
  incomeAssessment: WorkspacePart<IncomeAssessment>
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value)
const opaque = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 512
const part = (value: unknown): value is Record<string, unknown> => object(value) && (
  (value.status === "available" && object(value.value)) ||
  (value.status === "unavailable" && value.value == null && opaque(value.code)))

/** Validate the shared basis without normalizing, cloning or dropping fields/duplicate rows. */
export function isBenefitWorkspace(value: unknown, scopeKey: string, benefitId: string): value is BenefitWorkspace {
  if (!object(value) || value.contractVersion !== 1 || value.scopeKey !== scopeKey || !opaque(value.readStamp)
    || !object(value.benefit) || value.benefit.id !== benefitId || !opaque(value.benefit.concurrencyToken)
    || !part(value.workflow) || !part(value.incomeAssessment)) return false
  if (value.workflow.status === "available" && !["Legacy", "V2"].includes(String((value.workflow.value as Record<string, unknown>).authority))) return false
  if (value.incomeAssessment.status === "available"
    && (value.incomeAssessment.value as Record<string, unknown>).concurrencyToken !== value.benefit.concurrencyToken) return false
  return true
}

export function workspaceCanAuthorize(workspace: BenefitWorkspace | null | undefined) {
  return !!workspace && workspace.workflow.status === "available" && ["Legacy", "V2"].includes(workspace.workflow.value.authority) && !workspace.workflow.value.display?.readUnavailable
}
